import { useCallback, useEffect, useState } from "react";

export function usePagination(
  fetchFunction,
  itemsPerPage = 20,
  filters = {},
  enabled = true,
) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const [pageInfo, setPageInfo] = useState({
    endCursor: null,
    hasNextPage: false,
  });

  const [cursors, setCursors] = useState({
    1: null,
  });

  const fetchPage = async (pageNumber = 1, { silent = false } = {}) => {
    if (!enabled) {
      return;
    }

    if (!silent) {
      setLoading(true);
    }

    setError(null);

    const cursor = cursors[pageNumber];

    try {
      const result = await fetchFunction({
        first: itemsPerPage,
        after: cursor,
        ...filters,
      });

      const nodes = (result.edges || [])
        .map((edge) => edge.node)
        .filter(Boolean);

      setOptions(nodes);

      setPageInfo(
        result.pageInfo || {
          endCursor: null,
          hasNextPage: false,
        },
      );

      setCursors((prev) => ({
        ...prev,
        [pageNumber + 1]: result.pageInfo?.endCursor || null,
      }));
    } catch (err) {
      console.error(err);
      setError(err);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);

    fetchPage(newPage);
  };

  useEffect(() => {
    setPage(1);
    setCursors({
      1: null,
    });

    fetchPage(1);
  }, [enabled, JSON.stringify(filters)]);

  const refetch = useCallback(
    ({ silent = false } = {}) =>
      fetchPage(page, {
        silent,
      }),
    [page, fetchFunction, JSON.stringify(filters), enabled],
  );

  return {
    options,
    loading,
    error,
    page,
    pageInfo,
    handlePageChange,
    refetch,
  };
}
